import React from 'react';
import { useQuery } from 'react-query';
import { api } from '../lib/api';
import { Award } from 'lucide-react';

interface SubjectProgressData {
  totalTasks: number;
  completedTasks: number;
  progressPercentage: number;
  // Neue Felder für Zusatzaufgaben-Statistiken
  regularTasks?: number;
  completedRegularTasks?: number;
  baseProgress?: number;
  bonusTasks?: number;
  completedBonusTasks?: number;
  bonusProgress?: number;
  learningPlansCount?: number;
  learningPlans: Array<{
    LPID: number;
    NAME: string;
    START_DATUM: string;
    END_DATUM: string;
  }>;
  error?: string;
}

interface StudentProgressData {
  unid: string;
  overallProgress: {
    totalTasks: number;
    completedTasks: number;
    progressPercentage: number;
    regularTasks?: number;
    completedRegularTasks?: number;
    baseProgress?: number;
    bonusTasks?: number;
    completedBonusTasks?: number;
    bonusProgress?: number;
  };
  subjectProgress: {
    Deutsch: SubjectProgressData;
    Mathematik: SubjectProgressData;
    Englisch: SubjectProgressData;
  };
}

interface SubjectProgressOverviewProps {
  unid: string;
}

const subjectColors = {
  Deutsch: {
    bg: 'bg-blue-50 dark:bg-blue-950/35',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-900 dark:text-blue-100',
    progress: 'bg-blue-600 dark:bg-blue-500',
    barTrack: 'bg-white/50 dark:bg-gray-800/80',
  },
  Mathematik: {
    bg: 'bg-green-50 dark:bg-green-950/35',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-900 dark:text-green-100',
    progress: 'bg-green-600 dark:bg-green-500',
    barTrack: 'bg-white/50 dark:bg-gray-800/80',
  },
  Englisch: {
    bg: 'bg-purple-50 dark:bg-purple-950/35',
    border: 'border-purple-200 dark:border-purple-800',
    text: 'text-purple-900 dark:text-purple-100',
    progress: 'bg-purple-600 dark:bg-purple-500',
    barTrack: 'bg-white/50 dark:bg-gray-800/80',
  }
};

const subjectIcons = {
  Deutsch: '📚',
  Mathematik: '🔢',
  Englisch: '🌍'
};

export default function SubjectProgressOverview({ unid }: SubjectProgressOverviewProps) {
  const { data: progressData, isLoading, error } = useQuery<StudentProgressData>(
    ['student-progress', unid],
    async () => {
      const response = await api.get(`/lernbuero/student-progress/${unid}`);
      return response.data.data;
    },
    {
      enabled: !!unid,
      retry: 3,
      onError: (error) => {
        console.error('Fehler beim Laden der fächerübergreifenden Fortschritts-Daten:', error);
      }
    }
  );

  if (isLoading) {
    return (
      <div className="mt-6 rounded-lg bg-gray-50 p-4 dark:bg-gray-800/80">
        <div className="flex items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600 dark:border-primary-400" />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Lade Fächer-Fortschritt...</span>
        </div>
      </div>
    );
  }

  if (error || !progressData) {
    return (
      <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/35">
        <div className="flex items-center">
          <div className="text-red-600 dark:text-red-400">⚠️</div>
          <span className="ml-2 text-red-800 dark:text-red-200">Fehler beim Laden der Fächer-Fortschritts-Daten</span>
        </div>
      </div>
    );
  }

  const { overallProgress, subjectProgress } = progressData;
  const overallBaseProgress = overallProgress.baseProgress ?? overallProgress.progressPercentage ?? 0;
  const overallBonusProgress = overallProgress.bonusProgress ?? 0;
  const overallTotalProgress = overallBaseProgress + overallBonusProgress;
  const clampedOverallBase = Math.min(overallBaseProgress, 100);

  return (
    <div className="space-y-3">
      {/* Kompakter Gesamtfortschritt */}
      <div className="rounded-lg border border-primary-200 bg-gradient-to-r from-primary-50 to-primary-100 p-3 dark:border-primary-800 dark:from-primary-950/50 dark:to-primary-900/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Award className="mr-2 h-4 w-4 text-primary-600 dark:text-primary-400" />
            <h3 className="text-sm font-semibold text-primary-900 dark:text-primary-100">Gesamtfortschritt</h3>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-primary-900 dark:text-primary-100">{overallTotalProgress}%</div>
            <div className="text-[11px] text-primary-600 dark:text-primary-400">
              Basis {overallBaseProgress}%{overallBonusProgress > 0 ? ` • Bonus +${overallBonusProgress}%` : ''}
            </div>
            <div className="text-xs text-primary-700 dark:text-primary-300">
              {overallProgress.regularTasks !== undefined && overallProgress.completedRegularTasks !== undefined
                ? `${overallProgress.completedRegularTasks}/${overallProgress.regularTasks}`
                : `${overallProgress.completedTasks}/${overallProgress.totalTasks}`
              }
              {overallProgress.bonusTasks && overallProgress.bonusTasks > 0 && (
                <span className="ml-1 text-amber-700 dark:text-amber-400">
                  +{overallProgress.completedBonusTasks}/{overallProgress.bonusTasks}⭐
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Gesamtfortschritts-Balken */}
        <div className="mt-2 h-2 w-full rounded-full bg-primary-200 dark:bg-primary-900/60">
          <div
            className="h-2 rounded-full bg-primary-600 transition-all duration-300 ease-in-out dark:bg-primary-500"
            style={{ width: `${clampedOverallBase}%` }}
          />
        </div>
      </div>

      {/* Kompakter Fächer-Fortschritt */}
      <div className="grid grid-cols-3 gap-2">
        {Object.entries(subjectProgress).map(([subject, data]) => {
          const colors = subjectColors[subject as keyof typeof subjectColors];
          const icon = subjectIcons[subject as keyof typeof subjectIcons];
          const baseProgress = data.baseProgress ?? data.progressPercentage ?? 0;
          const bonusProgress = data.bonusProgress ?? 0;
          const clampedBase = Math.min(baseProgress, 100);
          const remainingSpace = Math.max(0, 100 - clampedBase);
          const clampedBonus = Math.min(bonusProgress, remainingSpace);
          const learningPlanCount = data.learningPlans?.length ?? data.learningPlansCount ?? 0;
          const totalProgress = baseProgress + bonusProgress;

          return (
            <div
              key={subject}
              className={`rounded-lg border p-2 ${colors.bg} ${colors.border}`}
            >
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center">
                  <span className="mr-1 text-lg">{icon}</span>
                  <h4 className={`text-xs font-semibold ${colors.text}`}>{subject}</h4>
                </div>
                <div className="text-right">
                  {data.error ? (
                    <div className={`text-sm font-bold ${colors.text}`}>?</div>
                  ) : (
                    <>
                      <div className={`text-sm font-bold ${colors.text}`}>{totalProgress}%</div>
                      <div className={`text-[11px] ${colors.text} opacity-80`}>
                        Basis {baseProgress}%{bonusProgress > 0 ? ` • Bonus +${bonusProgress}%` : ''}
                      </div>
                      <div className={`text-xs ${colors.text} opacity-75`}>
                        {data.regularTasks !== undefined && data.completedRegularTasks !== undefined
                          ? `${data.completedRegularTasks}/${data.regularTasks}`
                          : `${data.completedTasks}/${data.totalTasks}`
                        }
                        {data.bonusTasks && data.bonusTasks > 0 && (
                          <span className="ml-1 text-amber-700 dark:text-amber-400">
                            +{data.completedBonusTasks}/{data.bonusTasks}⭐
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {data.error ? (
                <div className={`text-sm ${colors.text} opacity-75`}>
                  {data.error}
                </div>
              ) : (
                <>
                  {/* Fortschritts-Balken */}
                  <div className={`relative mb-2 h-2 w-full overflow-hidden rounded-full ${colors.barTrack}`}>
                    <div
                      className={`${colors.progress} h-2 rounded-full transition-all duration-300 ease-in-out`}
                      style={{ width: `${clampedBase}%` }}
                    />
                    {bonusProgress > 0 && (
                      <div
                        className="absolute inset-y-0 rounded-full bg-amber-400/70 transition-all duration-300 ease-in-out dark:bg-amber-500/60"
                        style={{
                          left: `${clampedBase}%`,
                          width: `${clampedBonus}%`
                        }}
                      />
                    )}
                  </div>

                  {/* Lernpläne Info */}
                  {learningPlanCount > 0 && (
                    <div className={`text-xs ${colors.text} opacity-75`}>
                      {learningPlanCount} Lernplan{learningPlanCount !== 1 ? 'e' : ''} aktiv
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
}
